import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { axios, APIBASE_REVIVERCAMERACONTROL } from "./ApiConfig";
import {
  Camera,
  ExtendedCamera,
  CreateCameraRequest,
  ImageWithDetection,
  LinkCameraRequest,
  UpdateCameraRequest,
  ContractWasteStream,
  MotionDetectScheduleRequest,
} from "@/types";

export const putUpdateCamera = async (id: number, req: UpdateCameraRequest) =>
  axios.put(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/${id}`, req);

export const postCreateCamera = async (companyId: number, req: CreateCameraRequest) =>
  axios.post(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/${companyId}`, req);

export const deleteCamera = async (cameraId: number, companyId: number) =>
  axios.delete(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/${cameraId}`, {
    params: { companyId },
  });

const getCamerasByCompanyId = async (companyId: number) =>
  axios.get<Camera[]>(`${APIBASE_REVIVERCAMERACONTROL}api/cameras`, {
    params: { companyId },
  });

export const useGetCamerasByCompanyId = (companyId: number, options?: Partial<UseQueryOptions<Camera[]>>) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<Camera[]>({
    queryKey: ["getCamerasByCompanyId", companyId],
    queryFn: async () => {
      const result = await getCamerasByCompanyId(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

const getCameraWithWasteStreamByCompanyId = async (companyId: number) =>
  axios.get<ExtendedCamera[]>(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/with-streams`, {
    params: { companyId },
  });

export const useGetCameraWithWasteStreamByCompanyId = (
  companyId: number,
  options?: Partial<UseQueryOptions<ExtendedCamera[]>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<ExtendedCamera[]>({
    queryKey: ["getCameraWithWasteStreamByCompanyId", companyId],
    queryFn: async () => {
      const result = await getCameraWithWasteStreamByCompanyId(companyId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postLinkCameraToWasteStream = async (cameraId: number, req: LinkCameraRequest) =>
  axios.post(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/${cameraId}/wastestream`, req);

const getCameraImagesWithDetections = async (contractId: number) =>
  axios.get<ImageWithDetection[]>(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/detections/${contractId}/annotated`, {
    params: { contractId },
  });

export const useGetCameraImagesWithDetections = (
  contractId: number,
  options?: Partial<UseQueryOptions<ImageWithDetection[]>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<ImageWithDetection[]>({
    queryKey: ["getCameraImagesWithDetections", contractId],
    queryFn: async () => {
      const result = await getCameraImagesWithDetections(contractId);
      return result.data;
    },
    ...queryOptions,
  });
};

export const postEnableCameraMotionSchedule = async (cameraId: number, req: MotionDetectScheduleRequest) =>
  axios.post(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/${cameraId}/motion-schedule`, req);

export const postDisableCameraMotionSchedule = async (cameraId: number) =>
  axios.post(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/${cameraId}/motion-schedule/disable`);

const getWasteStreamsByContractId = async (contractId: number) =>
  axios.get<ContractWasteStream[]>(`${APIBASE_REVIVERCAMERACONTROL}api/cameras/waste-streams`, {
    params: { contractId },
  });

export const useGetWasteStreamsByContractId = (
  contractId: number,
  options?: Partial<UseQueryOptions<ContractWasteStream[]>>
) => {
  const queryOptions = {
    refetchOnWindowFocus: false,
    ...options,
  };

  return useQuery<ContractWasteStream[]>({
    queryKey: ["getWasteStreamsByContractId", contractId],
    queryFn: async () => {
      const result = await getWasteStreamsByContractId(contractId);
      return result.data;
    },
    ...queryOptions,
  });
};
